/**
 * Cab Module – Admin panel at /admin/cabs.
 * Manage countries, cities, services, cab types, vehicles, drivers, pricing, bookings.
 * Dropdowns use existing APIs so Country/City/Service/CabType/Vehicle/Driver are linked by ID.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Routes, Route, NavLink } from "react-router-dom";
import axios from "axios";
import { FaCar, FaPlus, FaGlobe, FaCity, FaCog, FaList, FaMoneyBillWave, FaBook, FaFilePdf } from "react-icons/fa";
import AdminDocumentModal from "../AdminDocumentModal";
import AdminCustomCabModal from "../admin/AdminCustomCabModal";

const API_BASE = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";

const withCreds = { withCredentials: true };

// ----- Field label (human readable) -----
const fieldLabels = {
  name: "Name",
  code: "Code",
  countryId: "Country",
  cityId: "City",
  serviceTypeId: "Service type",
  cabTypeId: "Cab type",
  baseFare: "Base fare (₹)",
  baseKm: "Base KM",
  perKmRate: "Per KM rate (₹)",
  peakMultiplier: "Peak multiplier",
  nightMultiplier: "Night multiplier",
  timezone: "Timezone",
  description: "Description",
  registrationNumber: "Registration number",
  phone: "Phone",
  licenseNumber: "License number",
};

export default function AdminCabPage() {
  return (
    <div className="min-h-screen bg-gray-50 flex">
      <aside className="w-56 bg-white border-r border-gray-200 shadow-sm shrink-0">
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-800 flex items-center gap-2">
            <FaCar className="text-blue-600" /> Cab Admin
          </h2>
        </div>
        <nav className="p-3 space-y-0.5">
          {[
            { to: "/admin/cabs", end: true, label: "Overview", icon: FaList },
            { to: "/admin/cabs/countries", label: "Countries", icon: FaGlobe },
            { to: "/admin/cabs/cities", label: "Cities", icon: FaCity },
            { to: "/admin/cabs/services", label: "Services", icon: FaCog },
            { to: "/admin/cabs/cab-types", label: "Cab Types", icon: FaCar },
            // { to: "/admin/cabs/vehicles", label: "Vehicles", icon: FaCar },
            // { to: "/admin/cabs/drivers", label: "Drivers", icon: FaCar },
            // { to: "/admin/cabs/pricing", label: "Pricing", icon: FaMoneyBillWave },
            { to: "/admin/cabs/multi-pricing", label: "Bulk Pricing", icon: FaMoneyBillWave },
            { to: "/admin/cabs/bookings", label: "Bookings", icon: FaBook },
          ].map(({ to, end, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={!!end}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100"}`
              }
            >
              {Icon && <Icon className="opacity-80" />} {label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <main className="flex-1 p-6 overflow-auto">
        <Routes>
          <Route index element={<AdminCabOverview />} />
          <Route path="countries" element={<AdminCabSection key="countries" name="Countries" entityKey="countries" listApi="/api/cab/admin/countries" createApi="/api/cab/admin/countries" method="POST" fields={["name"]} tableColumns={[{ label: "Name", get: (i) => i.name }]} />} />
          <Route path="cities" element={<AdminCabCities />} />
          <Route path="services" element={<AdminCabSection key="services" name="Services" entityKey="services" listApi="/api/cab/admin/services" createApi="/api/cab/admin/services" method="POST" fields={["name", "cityId", "description"]} optionsApis={{ cityId: { listApi: "/api/cab/admin/cities", labelKey: "name" } }} tableColumns={[{ label: "Name", get: (i) => i.name }, { label: "City", get: (i) => i.cityId?.name || "—" }, { label: "Description", get: (i) => i.description || "—" }]} />} />
          <Route path="cab-types" element={<AdminCabSection key="cab-types" name="Cab Types" entityKey="cab-types" listApi="/api/cab/admin/cab-types" createApi="/api/cab/admin/cab-types" method="POST" fields={["name", "serviceTypeId", "description"]} optionsApis={{ serviceTypeId: { listApi: "/api/cab/admin/services", labelKey: "name", optional: true } }} tableColumns={[{ label: "Name", get: (i) => i.name }, { label: "Service", get: (i) => i.serviceTypeId?.name || "—" }, { label: "Description", get: (i) => i.description || "—" }]} />} />
          <Route path="vehicles" element={<AdminCabVehicles />} />
          <Route path="drivers" element={<AdminCabSection key="drivers" name="Drivers" entityKey="drivers" listApi="/api/cab/admin/drivers" createApi="/api/cab/admin/drivers" method="POST" fields={["name", "phone", "licenseNumber"]} tableColumns={[{ label: "Name", get: (i) => i.name }, { label: "Phone", get: (i) => i.phone }, { label: "License", get: (i) => i.licenseNumber || "—" }]} hideActions={true} />} />
          <Route path="pricing" element={<AdminCabPricing />} />
          <Route path="multi-pricing" element={<AdminCabMultiPricing />} />
          <Route path="bookings" element={<AdminCabBookings />} />
        </Routes>
      </main>
    </div>
  );
}

function AdminCabOverview() {
  const [counts, setCounts] = useState({});
  useEffect(() => {
    const apis = ["/api/cab/admin/countries", "/api/cab/admin/cities", "/api/cab/admin/services", "/api/cab/admin/bookings"];
    Promise.all(apis.map((u) => axios.get(API_BASE + u, withCreds).then((r) => r.data?.data?.length ?? 0).catch(() => 0)))
      .then(([a, b, c, d]) => setCounts({ countries: a, cities: b, services: c, bookings: d }));
  }, []);
  const cards = [
    { label: "Countries", value: counts.countries ?? "—" },
    { label: "Cities", value: counts.cities ?? "—" },
    { label: "Services", value: counts.services ?? "—" },
    { label: "Bookings", value: counts.bookings ?? "—" },
  ];
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Cab Module Overview</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map(({ label, value }) => (
          <div key={label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <p className="text-gray-500 text-sm font-medium">{label}</p>
            <p className="text-2xl font-bold mt-1 text-gray-900">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ----- Generic section with optional dropdown options -----
function AdminCabSection({ name, entityKey, listApi, createApi, method, fields, optionsApis = {}, tableColumns, hideActions = false }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const optionKeys = Object.keys(optionsApis);
  const [optionsMap, setOptionsMap] = useState({});
  useEffect(() => {
    const fetchAll = async () => {
      const next = {};
      for (const key of optionKeys) {
        const api = optionsApis[key]?.listApi;
        if (!api) continue;
        try {
          const r = await axios.get(API_BASE + api, withCreds);
          next[key] = r.data?.data || [];
        } catch {
          next[key] = [];
        }
      }
      setOptionsMap(next);
    };
    fetchAll();
  }, [listApi]);

  const fetchList = () => {
    setLoading(true);
    axios.get(API_BASE + listApi, withCreds).then((r) => setList(r.data?.data || [])).catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(fetchList, [listApi]);

  const handleCreateOrUpdate = (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = fields.reduce((acc, f) => {
      const v = form[f];
      if (v === "" || v == null) return acc;
      return { ...acc, [f]: v };
    }, {});
    
    const request = editingId 
      ? axios.put(`${API_BASE}/api/cab/admin/${entityKey}/${editingId}`, payload, withCreds)
      : axios.post(API_BASE + createApi, payload, withCreds);
      
    request
      .then(() => { setShowForm(false); setEditingId(null); setForm({}); fetchList(); })
      .catch((err) => alert(err.response?.data?.error || "Failed"))
      .finally(() => setSubmitting(false));
  };

  const handleEdit = (item) => {
    const editForm = {};
    fields.forEach(f => {
      // Handle populated fields like cityId which might be an object
      editForm[f] = (item[f] && typeof item[f] === 'object') ? item[f]._id : item[f];
    });
    setForm(editForm);
    setEditingId(item._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to completely delete this item?")) return;
    try {
      await axios.delete(`${API_BASE}/api/cab/admin/${entityKey}/${id}`, withCreds);
      fetchList();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete");
    }
  };

  const getOptionsForField = (field) => {
    const config = optionsApis[field];
    if (!config) return [];
    let opts = optionsMap[field] || [];
    if (config.filterByParent && form[config.filterByParent]) {
      const parentId = form[config.filterByParent];
      opts = opts.filter((o) => (o[config.filterByParent]?._id || o[config.filterByParent]) === parentId);
    }
    return opts;
  };

  const isNumberField = (f) => ["baseFare", "baseKm", "perKmRate", "peakMultiplier", "nightMultiplier"].includes(f);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{name}</h1>
        {!showForm && (
          <button type="button" onClick={() => { setForm({}); setEditingId(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm">
            <FaPlus /> Add
          </button>
        )}
      </div>
      {showForm && (
        <form onSubmit={handleCreateOrUpdate} className="mb-6 p-5 bg-white rounded-xl shadow-sm border border-gray-100 space-y-4">
          <h3 className="font-semibold text-gray-800">{editingId ? "Edit item" : "Add new"}</h3>
          {fields.map((f) => {
            const label = fieldLabels[f] || f;
            const hasDropdown = optionsApis[f];
            const opts = hasDropdown ? getOptionsForField(f) : [];
            return (
              <div key={f}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                {hasDropdown ? (
                  <select
                    value={form[f] || ""}
                    onChange={(e) => setForm((s) => ({ ...s, [f]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required={!optionsApis[f].optional}
                  >
                    <option value="">Select {label}</option>
                    {opts.map((o) => (
                      <option key={o._id} value={o._id}>
                        {o[optionsApis[f].labelKey || "name"]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={isNumberField(f) ? "number" : "text"}
                    step={isNumberField(f) ? "any" : undefined}
                    value={form[f] ?? ""}
                    onChange={(e) => setForm((s) => ({ ...s, [f]: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={label}
                  />
                )}
              </div>
            );
          })}
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={submitting} className="px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {editingId ? "Update" : "Save"}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); setForm({}); }} className="px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
          </div>
        </form>
      )}
      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="min-w-full">
            {tableColumns?.length > 0 && (
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {tableColumns.map((col) => (
                    <th key={col.label} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{col.label}</th>
                  ))}
                  {!hideActions && <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>}
                </tr>
              </thead>
            )}
            <tbody>
              {list.map((item) => (
                <tr key={item._id} className="border-b border-gray-100 hover:bg-gray-50/50">
                  {tableColumns?.length > 0 ? tableColumns.map((col) => (
                    <td key={col.label} className="px-4 py-3 text-sm text-gray-800">{col.get ? col.get(item) : item[col.key]}</td>
                  )) : (
                    <td className="px-4 py-3 text-sm">{item.name ?? item.code ?? item._id}</td>
                  )}
                  {!hideActions && (
                    <td className="px-4 py-3 text-right text-sm">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800 font-medium">Edit</button>
                        <button onClick={() => handleDelete(item._id)} className="text-red-500 hover:text-red-700 font-medium">Delete</button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <p className="p-4 text-gray-500 text-sm">No records yet. Add one above.</p>}
        </div>
      )}
    </div>
  );
}

// ----- Cities: Country dropdown -----
function AdminCabCities() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ countryId: "", name: "", timezone: "Asia/Kolkata" });
  const [submitting, setSubmitting] = useState(false);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    axios.get(API_BASE + "/api/cab/admin/countries", withCreds).then((r) => setCountries(r.data?.data || [])).catch(() => setCountries([]));
  }, []);
  const fetchList = () => {
    setLoading(true);
    axios.get(API_BASE + "/api/cab/admin/cities", withCreds).then((r) => setList(r.data?.data || [])).catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(fetchList, []);

  const handleCreateOrUpdate = (e) => {
    e.preventDefault();
    if (!form.countryId) { alert("Please select a country"); return; }
    setSubmitting(true);
    
    const payload = { countryId: form.countryId, name: form.name, timezone: form.timezone || "Asia/Kolkata" };
    const request = editingId
      ? axios.put(`${API_BASE}/api/cab/admin/cities/${editingId}`, payload, withCreds)
      : axios.post(API_BASE + "/api/cab/admin/cities", payload, withCreds);
      
    request.then(() => { 
      setShowForm(false); 
      setEditingId(null);
      setForm({ countryId: "", name: "", timezone: "Asia/Kolkata" }); 
      fetchList(); 
    }).catch((err) => alert(err.response?.data?.error || "Failed")).finally(() => setSubmitting(false));
  };

  const handleEdit = (item) => {
    setForm({
      countryId: (item.countryId && typeof item.countryId === 'object') ? item.countryId._id : item.countryId,
      name: item.name || "",
      timezone: item.timezone || "Asia/Kolkata"
    });
    setEditingId(item._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to completely delete this city?")) return;
    try {
      await axios.delete(`${API_BASE}/api/cab/admin/cities/${id}`, withCreds);
      fetchList();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Cities</h1>
        {!showForm && (
          <button type="button" onClick={() => { setForm({ countryId: "", name: "", timezone: "Asia/Kolkata" }); setEditingId(null); setShowForm(true); }} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm">
            <FaPlus /> Add City
          </button>
        )}
      </div>
      {showForm && (
        <form onSubmit={handleCreateOrUpdate} className="mb-6 p-5 bg-white rounded-xl shadow-sm border border-gray-100 space-y-4">
          <h3 className="font-semibold text-gray-800">{editingId ? "Edit city" : "Add new city"}</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <select value={form.countryId} onChange={(e) => setForm((s) => ({ ...s, countryId: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required>
              <option value="">Select country</option>
              {countries.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City name</label>
            <input type="text" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g. Mumbai" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
            <input type="text" value={form.timezone} onChange={(e) => setForm((s) => ({ ...s, timezone: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Asia/Kolkata" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={submitting} className="px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">
              {editingId ? "Update" : "Save"}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
          </div>
        </form>
      )}
      {loading ? <p className="text-gray-500">Loading…</p> : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">City</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Country</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Timezone</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr key={item._id} className="border-b border-gray-100 hover:bg-gray-50/50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.countryId?.name ?? item.countryId ?? "—"}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.timezone || "—"}</td>
                  <td className="px-4 py-3 text-right text-sm">
                    <div className="flex items-center justify-end gap-3">
                      <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800 font-medium">Edit</button>
                      <button onClick={() => handleDelete(item._id)} className="text-red-500 hover:text-red-700 font-medium">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <p className="p-4 text-gray-500 text-sm">No cities yet. Add a country first, then add cities.</p>}
        </div>
      )}
    </div>
  );
}

// ----- Vehicles: Cab type dropdown -----
function AdminCabVehicles() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ cabTypeId: "", name: "", registrationNumber: "" });
  const [submitting, setSubmitting] = useState(false);
  const [cabTypes, setCabTypes] = useState([]);

  useEffect(() => {
    axios.get(API_BASE + "/api/cab/admin/cab-types", withCreds).then((r) => setCabTypes(r.data?.data || [])).catch(() => setCabTypes([]));
  }, []);
  const fetchList = () => {
    setLoading(true);
    axios.get(API_BASE + "/api/cab/admin/vehicles", withCreds).then((r) => setList(r.data?.data || [])).catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(fetchList, []);

  const handleCreate = (e) => {
    e.preventDefault();
    if (!form.cabTypeId) { alert("Please select a cab type"); return; }
    setSubmitting(true);
    axios.post(API_BASE + "/api/cab/admin/vehicles", { cabTypeId: form.cabTypeId, name: form.name, registrationNumber: form.registrationNumber }, withCreds).then(() => { setShowForm(false); setForm({ cabTypeId: "", name: "", registrationNumber: "" }); fetchList(); }).catch((err) => alert(err.response?.data?.error || "Failed")).finally(() => setSubmitting(false));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Vehicles</h1>
        <button type="button" onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm">
          <FaPlus /> Add Vehicle
        </button>
      </div>
      {showForm && (
        <form onSubmit={handleCreate} className="mb-6 p-5 bg-white rounded-xl shadow-sm border border-gray-100 space-y-4">
          <h3 className="font-semibold text-gray-800">Add new vehicle</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cab type</label>
            <select value={form.cabTypeId} onChange={(e) => setForm((s) => ({ ...s, cabTypeId: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required>
              <option value="">Select cab type</option>
              {cabTypes.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle name</label>
            <input type="text" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="e.g. Sedan 01" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Registration number</label>
            <input type="text" value={form.registrationNumber} onChange={(e) => setForm((s) => ({ ...s, registrationNumber: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Optional" />
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={submitting} className="px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">Save</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
          </div>
        </form>
      )}
      {loading ? <p className="text-gray-500">Loading…</p> : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vehicle</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cab type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Registration</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr key={item._id} className="border-b border-gray-100 hover:bg-gray-50/50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.cabTypeId?.name ?? item.cabTypeId ?? "—"}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.registrationNumber || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <p className="p-4 text-gray-500 text-sm">No vehicles yet. Add a cab type first, then add vehicles.</p>}
        </div>
      )}
    </div>
  );
}

// ----- Pricing: Country, City (filtered by country), Service, Cab type dropdowns -----
function AdminCabPricing() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ countryId: "", cityId: "", serviceTypeId: "", cabTypeId: "", baseFare: "", baseKm: "", perKmRate: "", peakMultiplier: "1", nightMultiplier: "1" });
  const [submitting, setSubmitting] = useState(false);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [services, setServices] = useState([]);
  const [cabTypes, setCabTypes] = useState([]);

  useEffect(() => {
    axios.get(API_BASE + "/api/cab/admin/countries", withCreds).then((r) => setCountries(r.data?.data || [])).catch(() => setCountries([]));
    axios.get(API_BASE + "/api/cab/admin/cities", withCreds).then((r) => setCities(r.data?.data || [])).catch(() => setCities([]));
    axios.get(API_BASE + "/api/cab/admin/services", withCreds).then((r) => setServices(r.data?.data || [])).catch(() => setServices([]));
    axios.get(API_BASE + "/api/cab/admin/cab-types", withCreds).then((r) => setCabTypes(r.data?.data || [])).catch(() => setCabTypes([]));
  }, []);
  const citiesInCountry = useMemo(() => {
    if (!form.countryId) return [];
    return cities.filter((c) => (c.countryId?._id || c.countryId) === form.countryId);
  }, [cities, form.countryId]);

  const fetchList = () => {
    setLoading(true);
    axios.get(API_BASE + "/api/cab/admin/pricing", withCreds).then((r) => setList(r.data?.data || [])).catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(fetchList, []);

  const handleCreate = (e) => {
    e.preventDefault();
    if (!form.serviceTypeId) { alert("Please select service type"); return; }
    setSubmitting(true);
    const payload = {
      countryId: form.countryId || undefined,
      cityId: form.cityId || undefined,
      serviceTypeId: form.serviceTypeId,
      cabTypeId: form.cabTypeId || undefined,
      baseFare: Number(form.baseFare) || 0,
    };
    axios.post(API_BASE + "/api/cab/admin/pricing", payload, withCreds).then(() => { setShowForm(false); setForm({ countryId: "", cityId: "", serviceTypeId: "", cabTypeId: "", baseFare: "" }); fetchList(); }).catch((err) => alert(err.response?.data?.error || "Failed")).finally(() => setSubmitting(false));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Pricing rules</h1>
        <button type="button" onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm">
          <FaPlus /> Add rule
        </button>
      </div>
      {showForm && (
        <form onSubmit={handleCreate} className="mb-6 p-5 bg-white rounded-xl shadow-sm border border-gray-100 space-y-4">
          <h3 className="font-semibold text-gray-800">Add pricing rule</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <select value={form.countryId} onChange={(e) => setForm((s) => ({ ...s, countryId: e.target.value, cityId: "" }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="">Any</option>
                {countries.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <select value={form.cityId} onChange={(e) => setForm((s) => ({ ...s, cityId: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" disabled={!form.countryId}>
                <option value="">Any</option>
                {citiesInCountry.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service type</label>
              <select value={form.serviceTypeId} onChange={(e) => setForm((s) => ({ ...s, serviceTypeId: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required>
                <option value="">Select service</option>
                {services.map((s) => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cab type</label>
              <select value={form.cabTypeId} onChange={(e) => setForm((s) => ({ ...s, cabTypeId: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="">Any</option>
                {cabTypes.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Base fare (₹)</label>
              <input type="number" min="0" step="0.01" value={form.baseFare} onChange={(e) => setForm((s) => ({ ...s, baseFare: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Base KM</label>
              <input type="number" min="0" value={form.baseKm} onChange={(e) => setForm((s) => ({ ...s, baseKm: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Per KM (₹)</label>
              <input type="number" min="0" step="0.01" value={form.perKmRate} onChange={(e) => setForm((s) => ({ ...s, perKmRate: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Peak mult.</label>
              <input type="number" min="0.1" step="0.1" value={form.peakMultiplier} onChange={(e) => setForm((s) => ({ ...s, peakMultiplier: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Night mult.</label>
              <input type="number" min="0.1" step="0.1" value={form.nightMultiplier} onChange={(e) => setForm((s) => ({ ...s, nightMultiplier: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={submitting} className="px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">Save</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
          </div>
        </form>
      )}
      {loading ? <p className="text-gray-500">Loading…</p> : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Service</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cab type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Base fare</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Per KM</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr key={item._id} className="border-b border-gray-100 hover:bg-gray-50/50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.serviceTypeId?.name ?? "—"}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.cabTypeId?.name ?? "Any"}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{[item.countryId?.name, item.cityId?.name].filter(Boolean).join(", ") || "Any"}</td>
                  <td className="px-4 py-3 text-sm text-gray-800">₹{item.baseFare}</td>
                  <td className="px-4 py-3 text-sm text-gray-800">₹{item.perKmRate}/km</td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <p className="p-4 text-gray-500 text-sm">No pricing rules yet. Add services and cab types first.</p>}
        </div>
      )}
    </div>
  );
}

function AdminCabBookingEditModal({ booking, onClose, onSave }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedDate, setEditedDate] = useState(booking.pickupTime ? new Date(booking.pickupTime).toISOString().slice(0, 16) : "");
  const [editedAmount, setEditedAmount] = useState(booking.totalAmount || 0);
  const [editedPassengers, setEditedPassengers] = useState(booking.passengers || []);

  const handlePassengerChange = (index, field, value) => {
    const newPass = [...editedPassengers];
    newPass[index][field] = value;
    setEditedPassengers(newPass);
  };

  const handleSave = () => {
    onSave({
      pickupTime: editedDate,
      totalAmount: Number(editedAmount),
      passengers: editedPassengers
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col relative">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
          <h2 className="text-xl font-bold">{isEditing ? "Edit Booking Details" : "Booking Details"}</h2>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <button onClick={() => setIsEditing(true)} className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded text-sm font-medium hover:bg-indigo-100">
                Edit Details
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-4 text-sm">
            
            <div className="grid grid-cols-2 gap-4 border-b pb-3">
              <div><span className="font-semibold text-gray-600">Country:</span> <p>{booking.countryId?.name || "N/A"}</p></div>
              <div><span className="font-semibold text-gray-600">City:</span> <p>{booking.cityId?.name || "N/A"}</p></div>
            </div>

            <div className="grid grid-cols-1 gap-4 border-b pb-3">
              <div><span className="font-semibold text-gray-600">Service:</span> <p>{booking.serviceTypeId?.name || booking.customCabDetails?.tripType || "N/A"}</p></div>
            </div>

            <div className="grid grid-cols-2 gap-4 border-b pb-3">
              <div>
                <span className="font-semibold text-gray-600">Trip Date:</span> 
                {isEditing ? (
                  <input type="datetime-local" value={editedDate} onChange={e => setEditedDate(e.target.value)} className="block w-full mt-1 border rounded px-2 py-1" />
                ) : (
                  <p>{booking.pickupTime ? new Date(booking.pickupTime).toLocaleString() : "N/A"}</p>
                )}
              </div>
              <div>
                <span className="font-semibold text-gray-600">Total Price:</span>
                {isEditing ? (
                  <input type="number" value={editedAmount} onChange={e => setEditedAmount(e.target.value)} className="block w-full mt-1 border rounded px-2 py-1" />
                ) : (
                  <p className="font-bold text-blue-600">₹{booking.totalAmount}</p>
                )}
              </div>
            </div>

            <div className="border-b pb-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-gray-800 text-base">Passengers</h3>
                {isEditing && (
                  <button onClick={() => setEditedPassengers([...editedPassengers, { type: 'adult', name: '', age: '', gender: 'Male', mobileNumber: '' }])} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                    + Add Passenger
                  </button>
                )}
              </div>
              
              {editedPassengers.length === 0 ? <p className="text-gray-500">No passengers listed.</p> : (
                <div className="space-y-3">
                  {editedPassengers.map((p, i) => (
                    <div key={i} className="bg-gray-50 p-3 rounded border">
                      {isEditing ? (
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 relative pr-6">
                          <select value={p.type} onChange={e => handlePassengerChange(i, 'type', e.target.value)} className="border rounded px-1 py-1 text-xs">
                            <option value="adult">Adult</option>
                            <option value="senior">Senior</option>
                            <option value="child">Child</option>
                            <option value="infant">Infant</option>
                          </select>
                          <input type="text" placeholder="Name" value={p.name} onChange={e => handlePassengerChange(i, 'name', e.target.value)} className="border rounded px-2 py-1 text-xs sm:col-span-2" />
                          <input type="number" placeholder="Age" value={p.age} onChange={e => handlePassengerChange(i, 'age', e.target.value)} className="border rounded px-2 py-1 text-xs" />
                          <input type="text" placeholder="Phone" value={p.mobileNumber || ""} onChange={e => handlePassengerChange(i, 'mobileNumber', e.target.value)} className="border rounded px-2 py-1 text-xs" />
                          <button onClick={() => setEditedPassengers(editedPassengers.filter((_, idx) => idx !== i))} className="absolute right-0 top-1 text-red-500 font-bold">&times;</button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-800">{p.name} <span className="text-xs text-gray-500">({p.type}, {p.age}y)</span></p>
                          <p className="text-gray-600 text-xs">{p.mobileNumber}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
          </div>
        </div>

        {/* Footer */}
        {isEditing && (
          <div className="p-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 shrink-0">
            <button onClick={() => {
              setIsEditing(false);
              setEditedDate(booking.pickupTime ? new Date(booking.pickupTime).toISOString().slice(0, 16) : "");
              setEditedAmount(booking.totalAmount || 0);
              setEditedPassengers(booking.passengers || []);
            }} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100">Cancel</button>
            <button onClick={handleSave} className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700">Save Changes</button>
          </div>
        )}

      </div>
    </div>
  );
}

// ----- Bookings: assign driver + vehicle, update status -----
function AdminCabBookings() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [docModalOpen, setDocModalOpen] = useState(false);
  const [selectedBookingForDoc, setSelectedBookingForDoc] = useState(null);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedBookingForView, setSelectedBookingForView] = useState(null);

  useEffect(() => {
    axios.get(API_BASE + "/api/cab/admin/bookings", withCreds).then((r) => setList(r.data?.data || [])).catch(() => setList([])).finally(() => setLoading(false));
    axios.get(API_BASE + "/api/cab/admin/drivers", withCreds).then((r) => setDrivers(r.data?.data || [])).catch(() => setDrivers([]));
    axios.get(API_BASE + "/api/cab/admin/vehicles", withCreds).then((r) => setVehicles(r.data?.data || [])).catch(() => setVehicles([]));
  }, []);

  const assignDriverAndVehicle = (bookingId, driverId, vehicleId) => {
    if (!driverId && !vehicleId) return;
    setAssigning(bookingId);
    axios.put(API_BASE + `/api/cab/admin/assign-driver/${bookingId}`, { driverId: driverId || undefined, vehicleId: vehicleId || undefined }, withCreds).then(() => {
      setList((prev) => prev.map((b) => (b._id === bookingId ? { ...b, driverId: driverId || b.driverId, vehicleId: vehicleId || b.vehicleId, status: "assigned" } : b)));
    }).catch((err) => alert(err.response?.data?.error || "Failed")).finally(() => setAssigning(null));
  };

  const updateStatus = (bookingId, status) => {
    axios.put(API_BASE + `/api/cab/admin/booking-status/${bookingId}`, { status }, withCreds).then(() => setList((prev) => prev.map((b) => (b._id === bookingId ? { ...b, status } : b)))).catch((err) => alert(err.response?.data?.error || "Failed"));
  };

  const deleteBooking = (bookingId) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    axios.delete(API_BASE + `/api/cab/admin/bookings/${bookingId}`, withCreds)
      .then(() => setList(prev => prev.filter(b => b._id !== bookingId)))
      .catch(err => alert(err.response?.data?.error || "Failed to delete booking"));
  };

  const handleUpload = async (bookingId, e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("eTicket", file);
    setUploading(bookingId);
    try {
      const res = await axios.put(`${API_BASE}/api/cab/admin/bookings/${bookingId}/e-ticket`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      if (res.data.success) {
        setList((prev) => prev.map((b) => (b._id === bookingId ? { ...b, eTicketPdfPath: res.data.data.eTicketPdfPath } : b)));
        alert("Document uploaded successfully");
      } else {
        alert(res.data.error || "Failed to upload document");
      }
    } catch (err) {
      alert(err.response?.data?.error || "Upload failed");
    } finally {
      setUploading(null);
      e.target.value = null;
    }
  };

  if (loading) return <p className="text-gray-500">Loading…</p>;
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Cab Bookings</h1>
        {/* <button
          onClick={() => setShowCustomModal(true)}
          className="inline-flex items-center gap-2 bg-indigo-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg hover:bg-indigo-700 transition-colors shadow-sm font-medium text-sm"
        >
          <FaPlus /> Custom Booking
        </button> */}
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Ref</th>
              {/* <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Route</th> */}
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>

              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Update status</th>
            </tr>
          </thead>
          <tbody>
            {list.map((b) => (
              <tr key={b._id} className="border-b border-gray-100 hover:bg-gray-50/50">
                <td className="px-4 py-3 font-medium text-gray-800">
                  <div className="flex flex-col gap-1">
                    <span>{b.bookingReference}</span>
                    {b.isCustomCab && (
                      <span className="inline-flex w-fit px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-[10px] font-bold uppercase tracking-wide">
                        Admin
                      </span>
                    )}
                  </div>
                </td>
                {/* <td className="px-4 py-3 text-gray-700">
                  {b.isCustomCab ? (
                    <span title="Custom Route">{b.customCabDetails?.pickupCity || "N/A"} → {b.customCabDetails?.dropCity || "N/A"}</span>
                  ) : (
                    <span>{b.pickupAddress} → {b.dropAddress}</span>
                  )}
                </td> */}
                <td className="px-4 py-3 text-gray-600">{b.customerName || b.userId?.username || "—"}</td>
                <td className="px-4 py-3 font-medium text-gray-800">₹{b.totalAmount}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${b.status === "completed" ? "bg-green-100 text-green-800" : b.status === "cancelled" ? "bg-red-100 text-red-800" : b.status === "assigned" ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-800"}`}>{b.status}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedBookingForView(b);
                        setViewModalOpen(true);
                      }}
                      className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 px-2 py-1 rounded text-center"
                    >
                      View Details
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedBookingForDoc(b);
                        setDocModalOpen(true);
                      }}
                      className="text-xs bg-indigo-100 text-indigo-700 hover:bg-indigo-200 px-2 py-1 rounded text-center"
                    >
                      Manage Docs
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteBooking(b._id)}
                      className="text-xs bg-red-100 text-red-700 hover:bg-red-200 px-2 py-1 rounded text-center"
                    >
                      Delete
                    </button>
                  </div>
                </td>

                <td className="px-4 py-3">
                  <select value={b.status || ""} onChange={(e) => { const v = e.target.value; if (v) updateStatus(b._id, v); }} className="px-2 py-1.5 border border-gray-300 rounded text-xs">
                    <option value="">Status</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="assigned">Assigned</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list.length === 0 && <p className="p-4 text-gray-500 text-sm">No cab bookings yet.</p>}
      </div>
      <AdminDocumentModal
        isOpen={docModalOpen}
        onClose={() => {
          setDocModalOpen(false);
          setSelectedBookingForDoc(null);
        }}
        booking={selectedBookingForDoc}
        type="cab"
        onUploadSuccess={(updatedDocs) => {
          setSelectedBookingForDoc((prev) => ({ ...prev, adminDocuments: updatedDocs }));
          setList((prev) => prev.map((x) => (x._id === selectedBookingForDoc._id ? { ...x, adminDocuments: updatedDocs } : x)));
        }}
      />
      {showCustomModal && (
        <AdminCustomCabModal
          onClose={() => setShowCustomModal(false)}
          onCreated={() => {
            setShowCustomModal(false);
            axios.get(API_BASE + "/api/cab/admin/bookings", { withCredentials: true }).then((r) => setList(r.data?.data || []));
          }}
        />
      )}

      {/* View Details Modal */}
      {viewModalOpen && selectedBookingForView && (
        <AdminCabBookingEditModal
          booking={selectedBookingForView}
          onClose={() => { setViewModalOpen(false); setSelectedBookingForView(null); }}
          onSave={async (updates) => {
            try {
              const res = await axios.put(`${API_BASE}/api/cab/admin/bookings/${selectedBookingForView._id}`, updates, withCreds);
              if (res.data.success) {
                setList(prev => prev.map(b => b._id === selectedBookingForView._id ? res.data.data : b));
                setViewModalOpen(false);
                setSelectedBookingForView(null);
                alert("Booking updated successfully");
              }
            } catch (err) {
              alert(err.response?.data?.error || "Failed to update booking");
            }
          }}
        />
      )}
    </div>
  );
}

// ----- Multi Pricing: Bulk insert with multiple cab types -----
function AdminCabMultiPricing() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ countryId: "", cityId: "", serviceTypeId: "", cabTypeIds: [] });
  const [pricingForm, setPricingForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [services, setServices] = useState([]);
  const [cabTypes, setCabTypes] = useState([]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    axios.get(API_BASE + "/api/cab/admin/countries", withCreds).then((r) => setCountries(r.data?.data || [])).catch(() => setCountries([]));
    axios.get(API_BASE + "/api/cab/admin/cities", withCreds).then((r) => setCities(r.data?.data || [])).catch(() => setCities([]));
    axios.get(API_BASE + "/api/cab/admin/services", withCreds).then((r) => setServices(r.data?.data || [])).catch(() => setServices([]));
    axios.get(API_BASE + "/api/cab/admin/cab-types", withCreds).then((r) => setCabTypes(r.data?.data || [])).catch(() => setCabTypes([]));
  }, []);
  const citiesInCountry = useMemo(() => {
    if (!form.countryId) return [];
    return cities.filter((c) => (c.countryId?._id || c.countryId) === form.countryId);
  }, [cities, form.countryId]);

  const servicesInCity = useMemo(() => {
    if (!form.cityId) return [];
    return services.filter((s) => (s.cityId?._id || s.cityId) === form.cityId);
  }, [services, form.cityId]);

  const fetchList = () => {
    setLoading(true);
    axios.get(API_BASE + "/api/cab/admin/pricing", withCreds).then((r) => setList(r.data?.data || [])).catch(() => setList([])).finally(() => setLoading(false));
  };
  useEffect(fetchList, []);

  const toggleCabType = (id) => {
    setForm(s => {
      const isSelected = s.cabTypeIds.includes(id);
      const newIds = isSelected ? s.cabTypeIds.filter(x => x !== id) : [...s.cabTypeIds, id];
      if (!isSelected && !pricingForm[id]) {
        setPricingForm(p => ({ ...p, [id]: { baseFare: "" } }));
      }
      return { ...s, cabTypeIds: newIds };
    });
  };

  const handlePriceChange = (cabId, field, val) => {
    setPricingForm(p => ({ ...p, [cabId]: { ...p[cabId], [field]: val } }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.serviceTypeId) { alert("Please select service type"); return; }
    if (form.cabTypeIds.length === 0) { alert("Please select at least one cab type"); return; }
    setSubmitting(true);

    try {
      if (editingId) {
        const cabId = form.cabTypeIds[0];
        const pForm = pricingForm[cabId];
        const payload = {
          countryId: form.countryId || undefined,
          cityId: form.cityId || undefined,
          serviceTypeId: form.serviceTypeId,
          cabTypeId: cabId || undefined,
          baseFare: Number(pForm?.baseFare) || 0,
        };
        await axios.put(API_BASE + `/api/cab/admin/pricing/${editingId}`, payload, withCreds);
      } else {
        const promises = form.cabTypeIds.map(cabId => {
          const pForm = pricingForm[cabId];
          const payload = {
            countryId: form.countryId || undefined,
            cityId: form.cityId || undefined,
            serviceTypeId: form.serviceTypeId,
            cabTypeId: cabId || undefined,
            baseFare: Number(pForm?.baseFare) || 0,
          };
          return axios.post(API_BASE + "/api/cab/admin/pricing", payload, withCreds);
        });
        await Promise.all(promises);
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ countryId: "", cityId: "", serviceTypeId: "", cabTypeIds: [] });
      setPricingForm({});
      fetchList();
    } catch (err) {
      alert("Failed to save pricing rule: " + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this pricing rule?")) return;
    try {
      await axios.delete(API_BASE + `/api/cab/admin/pricing/${id}`, withCreds);
      fetchList();
    } catch (err) {
      alert("Failed to delete: " + (err.response?.data?.error || err.message));
    }
  };

  const startEdit = (item) => {
    setShowForm(true);
    setEditingId(item._id);
    const cabId = item.cabTypeId?._id || item.cabTypeId || "";
    setForm({
      countryId: item.countryId?._id || item.countryId || "",
      cityId: item.cityId?._id || item.cityId || "",
      serviceTypeId: item.serviceTypeId?._id || item.serviceTypeId || "",
      cabTypeIds: cabId ? [cabId] : [],
    });
    if (cabId) {
      setPricingForm({
        [cabId]: { baseFare: item.baseFare }
      });
    } else {
      setPricingForm({});
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Bulk Pricing Rules</h1>
        <button type="button" onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium shadow-sm">
          <FaPlus /> Add bulk rules
        </button>
      </div>
      {showForm && (
        <div className="mb-6 p-5 bg-white rounded-xl shadow-sm border border-gray-100 space-y-4">
          <h3 className="font-semibold text-gray-800">Add multiple pricing rules</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <select value={form.countryId} onChange={(e) => setForm((s) => ({ ...s, countryId: e.target.value, cityId: "" }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option value="">Any</option>
                {countries.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <select value={form.cityId} onChange={(e) => setForm((s) => ({ ...s, cityId: e.target.value, serviceTypeId: "" }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" disabled={!form.countryId}>
                <option value="">Any</option>
                {citiesInCountry.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Service type</label>
              <select value={form.serviceTypeId} onChange={(e) => setForm((s) => ({ ...s, serviceTypeId: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required disabled={!form.cityId}>
                <option value="">Select service</option>
                {servicesInCity.map((s) => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Cab Types (Multi-select)</label>
            <div className="flex flex-wrap gap-2">
              {cabTypes
                .filter((c) => !form.serviceTypeId || (c.serviceTypeId?._id || c.serviceTypeId) === form.serviceTypeId)
                .map(c => {
                const isSelected = form.cabTypeIds.includes(c._id);
                return (
                  <button
                    key={c._id}
                    type="button"
                    onClick={() => toggleCabType(c._id)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${isSelected ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'}`}
                  >
                    {c.name}
                  </button>
                )
              })}
            </div>
          </div>

          {form.cabTypeIds.length > 0 && (
            <div className="mt-6 space-y-6 border-t pt-4">
              <h4 className="font-semibold text-gray-700">Set Prices for Selected Cabs</h4>
              {form.cabTypeIds.map(cabId => {
                const cabInfo = cabTypes.find(c => c._id === cabId);
                const pForm = pricingForm[cabId] || { baseFare: "", baseKm: "", perKmRate: "", peakMultiplier: "1", nightMultiplier: "1" };
                return (
                  <div key={cabId} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <h5 className="font-medium text-gray-800 mb-3">{cabInfo?.name} Pricing</h5>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
                      <input type="number" min="0" step="0.01" value={pForm.baseFare} onChange={(e) => handlePriceChange(cabId, 'baseFare', e.target.value)} className="w-full md:w-1/3 px-3 py-2 border border-gray-300 rounded-lg" placeholder="Enter total price" required />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <button type="button" onClick={handleCreate} disabled={submitting || form.cabTypeIds.length === 0} className="px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50">{editingId ? "Update Rule" : "Save All"}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      )}
      {loading ? <p className="text-gray-500">Loading…</p> : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Service</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cab type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Price</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item) => (
                <tr key={item._id} className="border-b border-gray-100 hover:bg-gray-50/50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{item.serviceTypeId?.name ?? "—"}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{item.cabTypeId?.name ?? "Any"}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{[item.countryId?.name, item.cityId?.name].filter(Boolean).join(", ") || "Any"}</td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-800">
                    ₹{item.baseFare}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <button onClick={() => startEdit(item)} className="text-blue-600 hover:text-blue-800 font-medium text-xs bg-blue-50 px-2 py-1 rounded">Edit</button>
                      <button onClick={() => handleDelete(item._id)} className="text-red-600 hover:text-red-800 font-medium text-xs bg-red-50 px-2 py-1 rounded">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.length === 0 && <p className="p-4 text-gray-500 text-sm">No pricing rules yet. Add services and cab types first.</p>}
        </div>
      )}
    </div>
  );
}
